import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TransferList } from '../../ui';
import ExportSectionRow from './ExportSectionRow';
import {
  DEFAULT_EXPANDED_GROUPS,
  columnsSummary,
  defaultSettings,
  toTransferGroups,
} from './packageExportUtils';

/**
 * Columns section: a summary line ("11 columns selected · Edit") that expands
 * in place to the full-width TransferList (plan §4.5, §4.6).
 */
const ExportColumnsSection = ({
  catalog,
  loading,
  loadError,
  onRetry,
  settings,
  leftOut,
  onChange,
  disabled,
}) => {
  const theme = useTheme();
  const { ft } = theme.palette;
  const isPeriod = settings.mode === 'between';
  const columns = settings.columns || [];
  const included = columns.length - leftOut.length;
  const expanded = settings.columnsExpanded;

  const groups = useMemo(
    () => (catalog ? toTransferGroups(catalog, isPeriod) : []),
    [catalog, isPeriod]
  );

  let summary;
  if (loadError) summary = 'Columns could not be loaded.';
  else if (loading) summary = 'Loading columns…';
  else summary = columnsSummary(included, leftOut.length);

  const textButtonSx = {
    height: 32,
    minWidth: 0,
    px: '6px',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.primary.main,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <ExportSectionRow label="Columns" alignCenter>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minHeight: 32 }}>
          <Typography
            component="span"
            sx={{ fontSize: 14, color: loadError ? ft.tones.error.text : ft.neutrals.textPrimary }}
          >
            {summary}
          </Typography>
          <Typography component="span" aria-hidden="true" sx={{ color: ft.neutrals.textDisabled }}>
            ·
          </Typography>
          {loadError ? (
            <Button variant="text" onClick={onRetry} sx={textButtonSx}>
              Try again
            </Button>
          ) : (
            <Button
              variant="text"
              aria-expanded={expanded}
              disabled={disabled}
              onClick={() => onChange({ columnsExpanded: !expanded })}
              sx={textButtonSx}
            >
              {expanded ? 'Done' : 'Edit'}
            </Button>
          )}
        </Box>
      </ExportSectionRow>

      {expanded && !loadError && (
        <Box sx={disabled ? { pointerEvents: 'none', opacity: 0.6 } : undefined} aria-disabled={disabled || undefined}>
          <TransferList
            groups={groups}
            selectedIds={columns}
            onChange={(next) => onChange({ columns: next })}
            sortable
            excludedIds={leftOut}
            defaultExpandedGroupIds={DEFAULT_EXPANDED_GROUPS}
            availableTitle="Available columns"
            availableCaption="Check a column to add it"
            selectedTitle={`Selected (${included})`}
            selectedCaption="Drag to set the file's column order"
            emptyText="No columns selected. Pick columns on the left."
            loading={loading}
            onReset={() => onChange({ columns: defaultSettings(catalog).columns })}
            onClear={() => onChange({ columns: [] })}
          />
        </Box>
      )}
    </Box>
  );
};

export default ExportColumnsSection;
